import { DragDropContext, DropResult } from '@hello-pangea/dnd'
import { Box } from '@mui/material'
import { equals, move } from 'ramda'
import React from 'react'

import RightDrawer from '@/app/component/atom/right-drawer'
import { Part } from '@/app/module/campaigns/types'

import CampaignNavigator from './campaign-navigator'

type Props = {
  activePart: string | undefined
  canReorder: boolean
  errorsByPart?: Record<string, string>
  open: boolean
  parts: Part[]

  onClose: () => void
  onOpen: () => void
  onPartClick: (partId: string) => void
  onPartReorder: (fromIndex: number, toIndex: number) => void
}

export const CAMPAIGN_DRAWER_WIDTH = 300

const CampaignDrawer: React.FC<Props> = ({
  activePart,
  canReorder,
  errorsByPart,
  open,
  parts,
  onClose,
  onOpen,
  onPartClick,
  onPartReorder,
}) => {
  const [localParts, setLocalParts] = React.useState<Part[]>(parts)

  React.useEffect(() => {
    setLocalParts((s) => {
      if (equals(s, parts)) {
        return s
      }
      return parts
    })
  }, [parts])

  const handleDragEnd = React.useCallback(
    (result: DropResult) => {
      if (typeof result.destination?.index !== 'number') {
        return
      }
      // do the reordering locally first, then call the callback so that the drawer don't have to wait for the parent to update
      setLocalParts((s) => {
        if (typeof result.destination?.index !== 'number' || result.source.index === result.destination.index) {
          return s
        }

        const fromIndex = result.source.index
        const toIndex = result.destination.index
        return move(fromIndex, toIndex, s)
      })

      onPartReorder(result.source.index, result.destination.index)
    },
    [onPartReorder],
  )

  return (
    <RightDrawer
      id="campaign-drawer"
      closeTooltip="Close content navigator"
      open={open}
      openButtonTop="84px"
      openTooltip="Open content navigator"
      width={CAMPAIGN_DRAWER_WIDTH}
      onClose={onClose}
      onOpen={onOpen}
    >
      {open && (
        <Box sx={{ flex: 1 }}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <CampaignNavigator
              activePart={activePart}
              canReorder={canReorder}
              errorsByPart={errorsByPart}
              parts={localParts}
              onPartClick={onPartClick}
            />
          </DragDropContext>
        </Box>
      )}
    </RightDrawer>
  )
}

export default CampaignDrawer
